import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import i18n from '../../../i18n';
import {User, UserConfig, UserWithConnection} from '../../../incase-domain/user';
import {IPaginatedResult, IPaginatedSearch} from '../../../incase-domain/search';
import {fetchLoggedInUser, fetchUsersForConnection, forgotPassword, login, resetPassword, updateUser} from '../../../service/authorization';
import {setLoggedInUserAction} from '../../../store/actions/auth';
import {startPageSpinnerAction, stopPageSpinnerAction} from '../../../store/actions/spinner';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import {QUERY_KEYS} from '../../../utils/queryKeys';

interface ResetForgotPasswordResponse {
    success: boolean;
    errors?: Record<string, string[]>;
}

interface ForgotPasswordVariables {
    email: string;
}

interface ResetPasswordVariables {
    password: string;
    repeatPassword: string;
    code: string;
}

export const useResetPassword = (): UseMutationResult<ResetForgotPasswordResponse, unknown, ResetPasswordVariables> => {
    const navigate = useNavigate();
    const {t} = useTranslation();

    return useMutation<ResetForgotPasswordResponse, unknown, ResetPasswordVariables>(
        (variables) => resetPassword(variables.password, variables.repeatPassword, variables.code),
        {
            onSuccess: (data) => {
                if (!data.errors) {
                    toast.success(t('toasts.passwordUpdated'));
                    navigate('/login');
                }
            },
        }
    );
};

interface LoginResult {
    user?: User;
    errors?: Record<string, string[]>;
}

interface LoginVariables {
    username: string;
    password: string;
}

interface UpdateUserVariables {
    user: User;
    userConfig: UserConfig;
}

type UpdateUserResult = {success: true; user: User} | {success: false; errors?: Record<string, string[]>};

export const useLoggedInUser = (): UseQueryResult<User | null> => {
    const dispatch = useDispatch();
    const loggedInUser = useSelector(loggedInUserSelector);

    return useQuery<User | null>({
        queryKey: QUERY_KEYS.auth.loggedInUser(),
        queryFn: async () => {
            dispatch(startPageSpinnerAction());
            try {
                const user = await fetchLoggedInUser();
                dispatch(setLoggedInUserAction(user ?? undefined));
                return user ?? null;
            } finally {
                dispatch(stopPageSpinnerAction());
            }
        },
        enabled: !loggedInUser,
        staleTime: Infinity,
        retry: false,
    });
};

export const useLogin = (): UseMutationResult<LoginResult, unknown, LoginVariables> => {
    const dispatch = useDispatch();

    return useMutation<LoginResult, unknown, LoginVariables>((variables) => login(variables), {
        onSuccess: (result) => {
            if (result.user) {
                dispatch(setLoggedInUserAction(result.user));
            }
        },
    });
};

export const useForgotPassword = (): UseMutationResult<ResetForgotPasswordResponse, unknown, ForgotPasswordVariables> => {
    const navigate = useNavigate();
    const {t} = useTranslation();

    return useMutation<ResetForgotPasswordResponse, unknown, ForgotPasswordVariables>((variables) => forgotPassword(variables.email), {
        onSuccess: (data) => {
            if (!data.errors) {
                toast.success(t('toasts.resetLinkSent'));
                navigate('/login');
            }
        },
    });
};

export const useUpdateUser = (): UseMutationResult<UpdateUserResult, unknown, UpdateUserVariables> => {
    const dispatch = useDispatch();

    return useMutation<UpdateUserResult, unknown, UpdateUserVariables>((variables) => updateUser(variables.user, variables.userConfig), {
        onSuccess: (result) => {
            if (result.success) {
                dispatch(setLoggedInUserAction(result.user));
                toast.success(i18n.t('toasts.successfullySaved'));
            } else {
                toast.error(i18n.t('toasts.updateUserFail'));
            }
        },
        onError: () => {
            toast.error(i18n.t('toasts.updateUserFail'));
        },
    });
};

export const useFetchUsersForConnection = (
    paginatedSearch: IPaginatedSearch
): UseQueryResult<IPaginatedResult<UserWithConnection> | null> => {
    return useQuery<IPaginatedResult<UserWithConnection> | null>({
        queryKey: QUERY_KEYS.auth.usersForConnection(paginatedSearch),
        queryFn: async () => {
            const result = await fetchUsersForConnection(paginatedSearch);
            return result ?? null;
        },
        keepPreviousData: true,
    });
};
